import { useIntl } from "react-intl";
import { useBudgetCreate } from "./hooks/useBudgetCreate";

import { BudgetVehicleCard } from "../components/BudgetVehicleCard";
import { ServiceSkeleton } from "../components/ServiceSkeleton";
import { BudgetHeader } from "../components/BudgetHeader";
import { BudgetSummary } from "../components/BudgetSummary";
import { BudgetServiceForm } from "../components/budgetServiceForm";
import { BudgetOccurrenceSelect } from "./components/BudgetOccurrenceSelect";
import { BudgetCreateAction } from "./components/BudgetCreateAction";

import "../index.css";

const SkeletonBlock = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse rounded bg-gray-300 ${className}`} />
);

interface IBudgetCreateProps {
  persona: string;
  onClose?: () => void;
  onBreadcrumbClick?: () => void;
  fixedOccurrenceId?: string;
  onCreated?: () => void;
}

const BudgetCreate = (props: IBudgetCreateProps) => {
  const intl = useIntl();
  const { persona, onClose, onBreadcrumbClick, fixedOccurrenceId, onCreated } = props;

  const {
    budget,
    occurrence,
    truckerPayPlan,
    profile,
    loadingOccurrence,
    submitting,
    canSendBudget,
    occurrenceId,
    setServices,
    addEmptyService,
    addEmptyItemToService,
    selectOccurrence,
    clearOccurrence,
    createBudget,
  } = useBudgetCreate(persona, { fixedOccurrenceId, onCreated });

  return (
    <div className="w-full">
      <BudgetHeader
        persona={persona}
        budget={budget}
        loading={false}
        showSentBy={false}
        showOccurrenceInfo={false}
        breadcrumbLabel={intl.formatMessage({ id: "intl-msg:budgets.new" })}
        onClose={onClose}
        onBreadcrumbClick={onBreadcrumbClick}
      />

      {!fixedOccurrenceId && (
        <BudgetOccurrenceSelect
          profile={profile}
          disabled={submitting}
          onSelect={selectOccurrence}
          onClear={clearOccurrence}
        />
      )}

      {occurrenceId ? (
        <>
          <BudgetVehicleCard occurrence={occurrence} loading={loadingOccurrence || !occurrence} />

          {!loadingOccurrence ? (
            <div className="mb-6 px-6">
              <div className="font-semibold mb-4 text-size-18">
                {intl.formatMessage({ id: "intl-msg:budgets.values" })}
              </div>
              {(budget.versionData?.services ?? []).map((service, index) => (
                <BudgetServiceForm
                  key={service.id || `service-${index}`}
                  budget={budget}
                  truckerPayPlan={truckerPayPlan}
                  isCreating
                  setService={(updatedService) => {
                    setServices(
                      budget.versionData?.services?.map((s, i) =>
                        i === index ? updatedService : s
                      ) ?? []
                    );
                  }}
                  onRemoveService={() => {
                    setServices(
                      budget.versionData?.services?.filter((_, i) => i !== index) ?? []
                    );
                  }}
                  onAddItem={() => addEmptyItemToService(index)}
                  serviceIndex={index}
                />
              ))}
              <button
                type="button"
                className="flex flex-row items-center text-gray-500 hover:text-gray-700 bg-transparent border-none p-0 cursor-pointer mt-2"
                onClick={addEmptyService}
              >
                <img
                  src="/assets/images/plus.svg"
                  alt={intl.formatMessage({ id: "intl-msg:budgets.addService" })}
                  className="w-5 h-5 flex-shrink-0 mr-2"
                />
                <span>{intl.formatMessage({ id: "intl-msg:budgets.addService" })}</span>
              </button>
            </div>
          ) : (
            <ServiceSkeleton />
          )}

          {!loadingOccurrence ? (
            <BudgetSummary persona={persona} budget={budget} truckerPayPlan={truckerPayPlan} />
          ) : (
            <SkeletonBlock className="h-[100px] w-full mb-4" />
          )}

          <BudgetCreateAction
            submitting={submitting}
            disabled={!canSendBudget}
            onCreate={createBudget}
          />
        </>
      ) : null}
    </div>
  );
};

export default BudgetCreate;
